import { Button, ButtonSizes, ButtonStyles, EmptyState, ListItem } from '@silverfort/sf-components';
import React, { useCallback, useMemo, useState } from 'react';
import { conditions, entity } from './types';
import { SCOPES_COLOR, SCOPES_TITLE } from './consts';
import EntitiesDualSelectList, { TAB_OPTIONS } from '../EntitiesDualSelectList';
import EquationPopUp from './EquationPopUp';
import CustomInsightsList from './CustomInsightsList';

interface ScopeProps {
    scope: number;
    emptyStateTitle: string;
    addEntityTitle?: string;
    emptyStateDescription: string;
    onAddNamingConvention: (conditionString: string, conditionType: number, scope: number) => void;
    deleteEntity: (insight: conditions | entity, scope: number) => void;
    onEditNamingConvention: (oldNamingConvention: conditions, newNamingConvention: conditions, scope: number) => void;
    onSelectEntities: (entities: entity[], scope: number) => void;
    entities: entity[];
    conditions: conditions[];
    tabOptions?: TAB_OPTIONS[];
    listWrapperClassName?: string;
}

const Scope: React.FC<ScopeProps> = React.memo(
    ({
        scope,
        emptyStateTitle,
        emptyStateDescription,
        onAddNamingConvention,
        deleteEntity,
        onEditNamingConvention,
        entities,
        conditions,
        onSelectEntities,
        tabOptions,
        listWrapperClassName,
        addEntityTitle = 'Add adObject'
    }) => {
        const [open, setOpen] = useState(false);
        const [showEquationPopUp, setShowEquationPopUp] = useState(false);
        const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
        const [menuOpen, setMenuOpen] = useState(false);
        const [namingConvention, setNamingConvention] = useState<conditions | undefined>(undefined);
        const onClickEditNamingConvention = (insight: conditions) => {
            setShowEquationPopUp(true);
            setNamingConvention(insight);
        };
        const onSelectEntitiesScope = useCallback(
            (entities) => {
                onSelectEntities(entities, scope);
            },
            [onSelectEntities]
        );
        const rows = useMemo(() => {
            return [...entities, ...conditions];
        }, [entities, conditions]);
        const entitiesGuids = useMemo(() => {
            return entities.map((o) => o.guid);
        }, [entities]);

        return (
            <div className="w-full border border-sf-gray-100 rounded-lg">
                <div className="relative items-center h-12 border-b border-sf-gray-100 px-4">
                    <div className="absolute left-4 top-3">
                        <div className="flex items-center gap-2">
                            <div className={`h-2 w-2 rounded-full ${SCOPES_COLOR[scope]}`}></div>
                            <div className="text-sf font-medium">{`${SCOPES_TITLE[scope]} (${rows.length})`}</div>
                        </div>
                    </div>
                    <div className="absolute top-3 right-4">
                        <Button
                            onClick={(e) => {
                                setShowEquationPopUp(false);
                                setMenuOpen(false);
                                setOpen((currOpen) => !currOpen);
                                setAnchorEl(e.currentTarget);
                                setNamingConvention(undefined);
                            }}
                            buttonStyle={ButtonStyles.Primary}
                            text="Add"
                            buttonSize={ButtonSizes.Small}
                        />
                        {open && (
                            <div
                                className={`absolute z-10 mt-2 w-fit rounded-lg bg-white p-2 shadow-sf-medium right-0`}>
                                <div className="w-[200px] rounded-lg flex flex-col">
                                    <ListItem
                                        title={addEntityTitle}
                                        onClick={() => {
                                            setShowEquationPopUp(false);
                                            setMenuOpen(true);
                                            setOpen(false);
                                        }}
                                    />
                                    <ListItem
                                        title="Add Naming convention"
                                        onClick={() => {
                                            setShowEquationPopUp(true);
                                            setOpen(false);
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {menuOpen && (
                            <EntitiesDualSelectList
                                title={SCOPES_TITLE[scope]}
                                entitiesTabsOptions={tabOptions}
                                onSelect={onSelectEntitiesScope}
                                selectedGuids={entitiesGuids}
                                onClose={() => {
                                    setMenuOpen(false);
                                }}
                                menuProps={{
                                    anchorEl: anchorEl,
                                    open: true,
                                    anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'right'
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'right'
                                    }
                                }}
                            />
                        )}
                        {showEquationPopUp && (
                            <EquationPopUp
                                defaultValue={namingConvention}
                                handleApply={(conditionString: string, conditionType: number) => {
                                    if (!namingConvention) {
                                        onAddNamingConvention(conditionString, conditionType, scope);
                                    } else {
                                        onEditNamingConvention(
                                            namingConvention,
                                            { conditionString, conditionType },
                                            scope
                                        );
                                    }
                                    setShowEquationPopUp(false);
                                }}
                                handleClose={() => {
                                    setShowEquationPopUp(false);
                                    setNamingConvention(undefined);
                                }}
                            />
                        )}
                    </div>
                </div>
                <div className={`overflow-y-auto no-scrollbar h-[232px] ${listWrapperClassName}`}>
                    {rows.length ? (
                        <div className="p-2">
                            <CustomInsightsList
                                onEdit={onClickEditNamingConvention}
                                customInsights={rows}
                                onDelete={(insight) => {
                                    deleteEntity(insight, scope);
                                }}></CustomInsightsList>
                        </div>
                    ) : (
                        <div className="h-full w-full flex flex-col justify-center items-center">
                            <EmptyState title={emptyStateTitle} description={emptyStateDescription} />
                        </div>
                    )}
                </div>
            </div>
        );
    }
);

export default Scope;
